import { EnrichCustomerResponse, EnrichedCustomer } from "~/types/enrich-customer";
import useFetch from "./useFetch";
import { Address } from "~/types/address";
import { getContext } from "~/helpers/tracking/trackers/tagular";
import { ReduxState } from "~/types/redux";
import { useDispatch, useSelector } from "react-redux";
import { setEnrichedCustomer } from "~/redux/actions/enrichedCustomer";
import { useEffect, useState } from "react";
import { setNewRelicNoticeError } from "~/helpers/newrelic";
import { DEFAULT_API_VERSION } from "~/constants/config/common";
import { useRouter } from "next/router";
import { getIPAddress } from "~/helpers/getIPAddress";

const useEnrichCustomer = (): {
  loading: boolean;
  data: EnrichCustomerResponse | null;
  runEnrichment: (address: Address) => Promise<EnrichedCustomer | Record<string, never>>;
} => {
  const router = useRouter();
  const dispatch = useDispatch();
  const {
    utmParams,
    isp,
    monarch,
    tracking: { clientSessionId },
  } = useSelector((store: ReduxState) => store);
  const apiVersion = (monarch?.apiVersion as string) || DEFAULT_API_VERSION;
  const [loading, setLoading] = useState(false);

  const { fetch, data, error } = useFetch<EnrichCustomerResponse>(
    `${apiVersion}/aicart/enrich-customer`,
    {
      method: "post",
    },
    true,
    true,
    true
  );

  useEffect(() => {
    if (error) {
      if (error instanceof Error) {
        setNewRelicNoticeError(new Error(`Error enriching customer: ${error.message}`));
      } else {
        setNewRelicNoticeError(new Error(`Error enriching customer: ${error.errorMessage}`));
      }
    }
  }, [error]);

  const runEnrichment = async (address: Address): Promise<EnrichedCustomer | Record<string, never>> => {
    setLoading(true);
    const atomContext = getContext();
    const { oidc } = router.query;
    const ipAddress = await getIPAddress();

    const data = await fetch({
      body: JSON.stringify({
        webContext: {
          anonymousId: atomContext.anonymousId,
          sessionId: atomContext.sessionId,
          instanceId: atomContext.instanceId,
          clientSessionId,
          utm_term: utmParams?.utm_term || "",
          utm_content: utmParams?.utm_content || "",
          utm_plan: utmParams?.utm_plan || "",
          utm_pp: utmParams?.utm_pp || "",
          userAgent: atomContext.userAgent,
          referrer: atomContext.pageReferrer,
          isp: isp || "",
          ipAddress,
        },
        address,
        ...(oidc === "true" ? { oidc: true } : {}),
      }),
    });

    if (data && data.results) {
      sessionStorage.setItem("customerEnrichment", JSON.stringify(data.results));
      dispatch(setEnrichedCustomer(data.results));
      setLoading(false);
      return data.results;
    }

    console.warn("Enrichment failed: runEnrichment returned null");
    dispatch(setEnrichedCustomer({}));
    setLoading(false);
    return {};
  };

  return {
    loading,
    data,
    runEnrichment,
  };
};

export default useEnrichCustomer;
